import { useCallback, useState } from 'react'

import {
  fetchToughCards,
  noLearningNeeded,
  recordAcquisition,
  type AcquisitionRound,
  type CardFilter,
  type LearningParameters,
  type ToughCard,
  type ToughStage,
} from '../../api/flashcards'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { Offer } from '../../ui/Offer'
import { Preview } from '../../ui/preview/Preview'
import { Failed, Waiting } from '../../ui/Status'

/**
 * The way into learning by heart: what is waiting, and the two ways of working through it.
 *
 * Nothing is worked on here. Typing runs as a session of its own that fills the screen (see
 * `RoteSession`), because acquiring wants the same undisturbed surface as answering questions. The one
 * list on this page is the pen-and-paper sheet, where having every line at once is the point.
 */
export function RoteLearning({
  filter,
  filterId,
  parameters,
  onStart,
  onFailure,
  onChanged,
}: {
  readonly filter: CardFilter
  readonly filterId?: string
  readonly parameters: LearningParameters
  readonly onStart: (nuts: readonly ToughCard[]) => void
  readonly onFailure: (failure: Failure | null) => void
  readonly onChanged: () => void
}) {
  const load = useLoad(
    useCallback(
      (signal: AbortSignal) => fetchToughCards(filter, filterId, signal),
      [filter, filterId],
    ),
  )
  const announce = useAnnounce()
  const [paper, setPaper] = useState(false)
  const [lines, setLines] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const attempt = async (work: () => Promise<void>) => {
    if (busy) return
    try {
      setBusy(true)
      onFailure(null)
      await work()
    } catch (error) {
      onFailure(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  if (load.load.status === 'waiting')
    return <Waiting label="Looking for cards to learn by heart." />
  if (load.load.status === 'failed') {
    return (
      <Failed
        failure={load.load.failure}
        onRetry={load.retry}
        title="The cards to learn by heart did not load"
      />
    )
  }

  const nuts = load.load.value
  const lineFor = (nut: ToughCard) => lines[nut.cardId] ?? ''

  if (paper) {
    return (
      <section className="rote-paper" aria-label="Pen and paper">
        <header className="card-section-head">
          <div>
            <p className="card-section-head__eyebrow">Pen and paper</p>
            <h3>{`Write each line ${parameters.drillRepetitions}× and say it out loud`}</h3>
            <p>
              Every line at once, because that is what you want in front of you while writing. The
              line is yours and is written afresh each time — MyLib proposes none and keeps none at
              the card.
            </p>
          </div>
          <Button
            onClick={() => {
              setPaper(false)
            }}
          >
            Back
          </Button>
        </header>
        <ul className="rote-sheet">
          {nuts.map((nut) => (
            <li key={`${nut.cardId}-${nut.direction}`}>
              <article className="rote-sheet__row">
                <div className="rote-sheet__sides">
                  <section>
                    <span>Question</span>
                    <Preview text={nut.question} format={nut.questionFormat} label="Question" />
                  </section>
                  <section>
                    <span>Answer</span>
                    <Preview text={nut.answer} format={nut.answerFormat} label="Answer" />
                  </section>
                </div>
                <label className="card-field">
                  <span className="card-field__label">Your line</span>
                  <input
                    className="card-field__control"
                    value={lineFor(nut)}
                    maxLength={300}
                    spellCheck={false}
                    placeholder="One line in your own words, e.g. 133 II BGB"
                    onChange={(event) => {
                      setLines((before) => ({ ...before, [nut.cardId]: event.target.value }))
                    }}
                  />
                </label>
                <footer className="rote-actions">
                  <Button
                    tone="bare"
                    disabled={busy}
                    onClick={() => {
                      void attempt(async () => {
                        await noLearningNeeded(nut.cardId)
                        announce('Marked as known. It will be asked like any other card.')
                        onChanged()
                        load.retry()
                      })
                    }}
                  >
                    No learning needed
                  </Button>
                  <Button
                    disabled={busy || lineFor(nut).trim() === ''}
                    onClick={() => {
                      void attempt(async () => {
                        await recordAcquisition(
                          nut.cardId,
                          parameters.drillRepetitions,
                          lineFor(nut).trim(),
                        )
                        announce('Written on paper. MyLib took note of it.')
                        onChanged()
                        load.retry()
                      })
                    }}
                  >
                    {`Wrote it ${parameters.drillRepetitions}×`}
                  </Button>
                </footer>
                {nut.lastAcquisition !== null && (
                  <p className="rote-sheet__acquired">{acquiredCopy(nut.lastAcquisition)}</p>
                )}
              </article>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  return (
    <section className="rote-learning" aria-label="Learn by heart">
      <header className="card-section-head">
        <div>
          <p className="card-section-head__eyebrow">Rote learning</p>
          <h3>Learn by heart</h3>
          <p>
            {nuts.length === 0
              ? 'Nothing in this selection needs acquiring right now.'
              : `${nuts.length} ${nuts.length === 1 ? 'card' : 'cards'} in this selection cannot be tested yet — nothing has been retrieved that a question could test. You go through them one at a time, and write your line afresh each session.`}
          </p>
        </div>
      </header>

      {nuts.length === 0 ? (
        <Offer
          title="Nothing to learn by heart here"
          lead="A card MyLib has never seen an answer to starts here, because asking it could only produce a shrug and a glance at the answer. One press of “No learning needed” during a session takes a card out again — and the history brings it back if it turns out otherwise."
          points={[
            'You write the mnemonic for every session; MyLib proposes and remembers none.',
            'Typing it from memory counts, copying something visible does not.',
            'An acquisition is never a retrieval: it only makes the next question worth asking.',
          ]}
        />
      ) : (
        <div className="rote-start">
          <div className="rote-stages">
            {STAGE_ORDER.map((stage) => (
              <span key={stage}>
                {`${nuts.filter((nut) => nut.stage === stage).length} ${STAGE_COPY[stage]}`}
              </span>
            ))}
          </div>
          <div className="rote-actions">
            <Button
              disabled={busy}
              onClick={() => {
                setLines({})
                setPaper(true)
              }}
            >
              Pen and paper
            </Button>
            <Button
              tone="primary"
              disabled={busy}
              onClick={() => {
                onStart(nuts)
              }}
            >
              {`Start · ${nuts.length}`}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

export const STAGE_COPY: Record<ToughStage, string> = {
  nothing_yet: 'never acquired',
  awaiting_retrieval: 'acquired, retrieval outstanding',
  acquired_earlier: 'acquired earlier, asked since',
}

const STAGE_ORDER: readonly ToughStage[] = ['nothing_yet', 'acquired_earlier', 'awaiting_retrieval']

function acquiredCopy(round: AcquisitionRound): string {
  const how = round.kind === 'on_paper' ? 'on paper' : 'typed'
  const when = round.endedAt === null ? '' : ` on ${new Date(round.endedAt).toLocaleDateString()}`
  return `Produced ${round.repetitionsDone}× ${how}${when}.`
}
